/**
 * Kompakt CV – sidmått, typskala, blockordning och radberäkning.
 *
 * Allt räknas i mm och pt så att skärmvyn och utskriften använder samma
 * geometri. Webbläsaren räknar om mm till px med 96 dpi, vilket gör att
 * scrollHeight på skärmen är jämförbar med den tryckta höjden.
 */

import { dedupeSoftware, hasSoftware } from '../utils/softwareNames';

/* ── Sidgeometri ─────────────────────────────────────────────────────── */

export const PAGE_WIDTH_MM = 210;
export const PAGE_HEIGHT_MM = 297;
export const PAGE_MARGIN_MM = 8;

export const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - 2 * PAGE_MARGIN_MM;   // 194
export const CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - 2 * PAGE_MARGIN_MM; // 281

export const mmToPx = mm => (mm * 96) / 25.4;
export const pxToMm = px => (px * 25.4) / 96;

/* ── Typskala ────────────────────────────────────────────────────────── */

export const BASE_PT = 7.5;

/** Stigande ordning – index används av auto-anpassningens nedskärningssteg. */
export const FONT_SCALES = [
  { value: 0.867, label: '6,5' },
  { value: 0.933, label: '7' },
  { value: 1, label: '7,5' },
  { value: 1.067, label: '8' },
  { value: 1.133, label: '8,5' },
  { value: 1.2, label: '9' },
];

/** Index för 7,5 pt – gränsen där auto-anpassningen börjar offra innehåll. */
export const BASE_SCALE_INDEX = 2;

/**
 * Täthet. Luftig är standard – ett CV som får plats ska andas. Tät sparar
 * ungefär en fjärdedel av höjden och är första steget när något inte ryms.
 */
export const DENSITIES = {
  luftig: { lineHeight: 1.28, rowPadMm: 0.4, gapMm: 0.7, label: 'Luftig' },
  tat: { lineHeight: 1.12, rowPadMm: 0.1, gapMm: 0.35, label: 'Tät' },
};

export const DENSITY_ORDER = ['luftig', 'tat'];

/** Höjden på en projektrad i mm för givna inställningar. */
export function rowMm(settings) {
  const d = DENSITIES[settings.density] || DENSITIES.luftig;
  return (BASE_PT * settings.fontScale * d.lineHeight * 25.4) / 72 + 2 * d.rowPadMm;
}

/* ── Projekttabellens kolumner ───────────────────────────────────────── */

/**
 * Två uppsättningar: med och utan budgetkolumn. Summan plus mellanrummen
 * ska landa på CONTENT_WIDTH_MM (194).
 * Rollkolumnen är den bredaste eftersom rolltitlar som "Projektledare
 * Digitala Arbetssätt, VDC/BIM-ansvarig" annars radbryter.
 */
export const COLS = {
  periodMm: 17,
  nameMm: 48,
  clientMm: 42,
  roleMm: 79,
  gapMm: 2.5,
};

export const COLS_WITH_BUDGET = {
  periodMm: 17,
  nameMm: 44,
  clientMm: 38,
  roleMm: 68,
  budgetMm: 16,
  gapMm: 2.5,
};

export function columnsFor(showBudget) {
  return showBudget ? COLS_WITH_BUDGET : COLS;
}

/** Ungefärlig teckenbudget per kolumn – används av AI-nedkortningen. */
export function colCharLimits(showBudget) {
  const c = columnsFor(showBudget);
  const perChar = 1.32; // mm per tecken vid 7,5 pt
  return {
    name: Math.floor(c.nameMm / perChar),
    client: Math.floor(c.clientMm / perChar),
    role: Math.floor(c.roleMm / perChar),
  };
}

/**
 * Etikettkolumnen är exakt lika bred som årskolumnen plus mellanrummet.
 * Därmed börjar all löptext i dokumentet på samma tabbstopp som
 * projektnamnen, och etiketterna linjerar med årtalen.
 * Konsekvens: etiketterna måste hållas under ~12 tecken.
 */
export const LABEL_WIDTH_MM = COLS.periodMm + COLS.gapMm;

/** Ungefärligt antal tecken på en run-in-rad efter etikettens indrag. */
export const CHARS_PER_LINE = 118;

/* ── Tillval, i prioritetsordning ────────────────────────────────────── */

/**
 * `label` är namnet i kontrollpanelen, `short` är etiketten i dokumentet.
 * Den korta måste rymmas i LABEL_WIDTH_MM i versaler.
 */
export const COMPACT_OPTIONAL_BLOCKS = [
  { id: 'personal_profile', label: 'Personlig profil', short: 'Profil' },
  { id: 'industries', label: 'Branscherfarenhet', short: 'Bransch' },
  { id: 'certifications', label: 'Certifieringar & övrig utb.', short: 'Certifikat' },
  { id: 'tools', label: 'IT & Verktyg', short: 'IT' },
  { id: 'standards', label: 'Standarder & Regelverk', short: 'Standarder' },
];

export const PAGE_CHOICES = [1, 2, 3];

export const OPTIONAL_ORDER = COMPACT_OPTIONAL_BLOCKS.map(b => b.id);

export const DEFAULT_COMPACT_SETTINGS = {
  pages: 2,
  optional: ['personal_profile', 'industries'],
  showIngress: true,
  splitLicences: false,     // bryt ut BAS-P/BAS-U m.fl. till en egen rad som alltid visas
  includeToolsBasic: false, // "grundläggande kunskaper" kostar en rad och säger lite
  zebra: true,
  showLogo: true,
  showPhoto: true,
  showBudget: true,
  showAssignmentImages: true,
  showProjectInfo: false,   // projektbeskrivningen före rollbeskrivningen i utfällda uppdrag
  showSoftware: false,      // mjukvaror som pills under texten i utfällda uppdrag
  markWrapped: false,      // orange streck vid radbrytning – bara på skärmen, av som standard
  pinned: [],               // tillval som kryssats i för hand – auto-anpassningen rör dem inte
  density: 'luftig',
  fontScale: 1,
  autoFit: true,
  maxIngressChars: 300,
};

/* ── Behörigheter kontra övriga utbildningar ─────────────────────────── */

const LICENCE_PATTERNS = [
  /bas[\s-]?[pu]\b/i,
  /säkerhet på väg/i,
  /heta arbeten/i,
  /\bid06\b/i,
  /körkort/i,
  /ställningsbygg/i,
  /\btruck\b/i,
  /\blift\b/i,
  /\bhlr\b/i,
  /första hjälpen/i,
  /\besa\b/i,
  /arbete på väg/i,
];

export const isLicence = name => LICENCE_PATTERNS.some(re => re.test(name || ''));

/* ── Radberäkning ────────────────────────────────────────────────────── */

/** Uppskattat antal rader en run-in-rad tar. */
export function estimateRows(text, fontScale = 1) {
  const len = (text || '').length;
  if (!len) return 0;
  return Math.max(1, Math.ceil(len / (CHARS_PER_LINE / fontScale)));
}

/* ── Uppdragsperiod ──────────────────────────────────────────────────── */

// ÅÅÅÅ eller ÅÅÅÅ/MM, bindestreck eller tankstreck, sedan slutdatum, ett ord
// som "pågående" eller ingenting.
const PERIOD_RANGE = /^(\d{4}(?:[/.-]\d{1,2})?)\s*[-–—]\s*(\d{4}(?:[/.-]\d{1,2})?|[A-Za-zÅÄÖåäö]+)?$/;

/**
 * Delar en period i de bitar som får radbryta sinsemellan, med tankstreck
 * och mellanslag oavsett hur intervallet är skrivet i profilen.
 * "2023/10-2024/03" → ["2023/10 –", "2024/03"], "2025 -" → ["2025 –"].
 * Perioder som inte är ett intervall lämnas orörda som en enda bit.
 * Påverkar bara visningen – det lagrade värdet ändras aldrig.
 */
export function periodParts(period) {
  const text = (period || '').trim();
  const m = text.match(PERIOD_RANGE);
  if (!m) return text ? [text] : [];
  return m[2] ? [`${m[1]} –`, m[2]] : [`${m[1]} –`];
}

/* ── Normalisering av en profil till kompakt form ────────────────────── */

const join = (parts, sep = ' · ') => parts.filter(Boolean).join(sep);

/**
 * IT-listans tre nivåer. Ett verktyg som står i flera nivåer eller i två
 * stavningar ("BIM 360", "BIM360") visas en gång, i den högsta nivån.
 */
function toolLevels(tools) {
  const seen = [];
  return ['expert', 'good', 'basic'].map(level => {
    const names = dedupeSoftware(tools?.[level]).filter(n => !hasSoftware(seen, n));
    seen.push(...names);
    return names.sort((a, b) => a.localeCompare(b, 'sv'));
  });
}

/**
 * Plockar ut exakt det kompakta läget renderar ur en profil.
 * Beskrivningsfälten på uppdragen (projectDescription, personalDescription,
 * description, image, software) läses bara för uppdrag som fällts ut.
 */
export function buildCompactPerson({ userId, profile, roleInTender, overrides }) {
  const p = profile || {};
  const ov = overrides || {};

  // `note` renderas inte i grundläget. Den finns med för att enstaka uppdrag
  // ska kunna fällas ut manuellt – roll och insats först, den säger mest i
  // ett anbud eftersom projektets identitet redan står i namnet.
  // `projectInfo` och `roleNote` är samma texter var för sig, för tillvalet
  // som visar projektbeskrivningen före rollbeskrivningen.
  const assignments = (ov.assignments || p.assignments || [])
    .map((a, i) => ({
      index: i,
      period: a.period || '',
      name: a.name || '',
      client: a.client || '',
      role: a.role || '',
      budget: a.budget || '',
      image: a.image || '',
      note: a.personalDescription || a.projectDescription || a.description || '',
      projectInfo: a.projectDescription || '',
      roleNote: a.personalDescription || '',
      software: dedupeSoftware(a.software),
    }))
    .filter(a => a.period || a.name);

  const certs = ov.certifications || p.certifications || [];
  const licences = certs.filter(c => isLicence(c.name));
  const otherCerts = certs.filter(c => !isLicence(c.name));

  const fmtCert = c => (c.year ? `${c.name} (${c.year})` : c.name);
  const [toolsExpert, toolsGood, toolsBasic] = toolLevels(p.tools);

  return {
    userId,
    name: p.personal?.name || '',
    title: p.personal?.title || '',
    photo: p.personal?.photo || '',
    meta: join([p.personal?.location, p.personal?.phone]),
    roleInTender: roleInTender || '',
    ingress: ov.ingress != null ? ov.ingress : p.ingress || '',

    personal_profile: join(p.personal_profile || []),
    industries: join(p.industries || []),
    competencies: join(p.competencies || []),
    education: join(
      (p.education || []).map(e => join([e.period, join([e.degree, e.school], ', ')], ' ')),
    ),
    employment: join(
      (p.employment || []).map(e => join([e.period, join([e.company, e.title], ', ')], ' ')),
    ),
    licences: join(licences.map(fmtCert)),
    certifications: join(certs.map(fmtCert)),
    otherCertifications: join(otherCerts.map(fmtCert)),
    toolsExpert,
    toolsGood,
    toolsBasic,
    standards: join(p.standards || []),
    languages: join(
      (p.languages || []).map(l => (l.level ? `${l.language} (${l.level})` : l.language)),
    ),
    assignments,
  };
}

/**
 * Lägger AI-nedkortningar ovanpå en person utan att röra originalprofilen.
 * ov = { ingress, assignments: { [index]: { name, client, role } }, dropped: [index] }
 */
export function applyOverrides(person, ov) {
  if (!ov) return person;
  return {
    ...person,
    ingress: ov.ingress != null ? ov.ingress : person.ingress,
    assignments: person.assignments
      .filter(a => !(ov.dropped || []).includes(a.index))
      .map(a => ({
        ...a,
        ...(ov.assignments?.[a.index] || {}),
        expanded: (ov.expanded || []).includes(a.index),
      })),
  };
}

/** Radkostnaden för ett tillval, summerat över alla personer. */
export function optionalBlockRows(blockId, persons, settings) {
  const s = settings.fontScale;
  return persons.reduce((sum, person) => {
    if (blockId === 'tools') {
      const parts = [
        ...person.toolsExpert,
        ...person.toolsGood,
        ...(settings.includeToolsBasic ? person.toolsBasic : []),
      ];
      return sum + estimateRows(join(parts), s);
    }
    if (blockId === 'certifications') {
      const text = settings.splitLicences ? person.otherCertifications : person.certifications;
      return sum + estimateRows(text, s);
    }
    return sum + estimateRows(person[blockId], s);
  }, 0);
}

/** Radkostnaden för mjukvarupills – ungefär en rad per åtta pills. */
export function softwareRows(persons) {
  return persons.reduce((sum, person) => sum + person.assignments
    .filter(a => a.expanded && a.software.length)
    .reduce((s, a) => s + Math.ceil(a.software.length / 8), 0), 0);
}

/** Radkostnaden för projektinfo – bara utfällda uppdrag visar den. */
export function projectInfoRows(persons, settings) {
  return persons.reduce((sum, person) => sum + person.assignments
    .filter(a => a.expanded)
    .reduce((s, a) => s + estimateRows(a.projectInfo, settings.fontScale), 0), 0);
}
